/**
 * Broadcast Notification Component
 * Displays a broadcast message notification
 */

import { EllipsisVertical } from "lucide-react"
import { Avatar } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"

interface BroadcastNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onDelete?: () => void
  onNotificationClick?: (notification: Notification) => void
}

interface BroadcastNotificationData {
  broadcasterId?: string
  broadcasterName?: string
  broadcasterUsername?: string
  broadcasterProfilePictureUrl?: string | null
  message?: string
}

function getUserDisplayImage(user: Notification["triggeredByUser"]): string | undefined {
  if (!user) return undefined
  if (user.profile_photos && user.profile_photos.length > 0) {
    const coverPhoto = user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
  }
  return user.profilePictureUrl ?? undefined
}

export function BroadcastNotification({
  notification,
  onRead,
  onUnread,
  onDelete,
  onNotificationClick,
}: BroadcastNotificationProps) {
  const data = notification.data as BroadcastNotificationData

  const message = data.message || ""
  const broadcaster = notification.triggeredByUser
  const isRead = notification.isRead

  const relativeTime = formatNotificationTime(notification.createdAt)

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  const handleDelete = () => {
    onDelete?.()
  }

  if (!broadcaster) {
    return null
  }

  const displayImageUrl = getUserDisplayImage(broadcaster)
  const broadcasterName = data.broadcasterName || broadcaster.name || "Someone"
  const broadcasterUsername = data.broadcasterUsername || broadcaster.username || "rituality"

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row: Logo, Brand, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Logo */}
          <Avatar src={displayImageUrl} fallback={broadcaster.name ?? undefined} size="lg" />

          {/* Brand Name and Message */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">{broadcasterName}</span>
              <span className="text-stone text-[14px]">@{broadcasterUsername}</span>
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px] whitespace-pre-wrap">
              {message}
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-1 -right-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleDelete()
                  }}
                  className="text-[11px] cursor-pointer text-red-500"
                >
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}
